import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Admin.css';

const PaymentLinksList = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await axios.get('http://localhost:5000/api/admin/payment-links');

      if (response.data.success) {
        setLinks(response.data.links);
      }
    } catch (err) {
      setError('Failed to fetch payment links');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (linkId) => {
    try {
      const response = await axios.delete(
        `http://localhost:5000/api/admin/payment-links/${linkId}`
      );

      if (response.data.success) {
        setLinks(links.filter(link => link.link_id !== linkId));
        setShowDeleteConfirm(null);
        showNotification('Payment link deleted successfully', 'success');
      }
    } catch (err) {
      showNotification('Failed to delete payment link', 'error');
    }
  };

  const handleStatusChange = async (linkId, newStatus) => {
    const link = links.find(l => l.link_id === linkId);

    try {
      const response = await axios.put(
        `http://localhost:5000/api/admin/payment-links/${linkId}`,
        { ...link, status: newStatus }
      );

      if (response.data.success) {
        setLinks(links.map(l => 
          l.link_id === linkId ? { ...l, status: newStatus } : l
        ));
        showNotification(`Link ${newStatus} successfully`, 'success');
      }
    } catch (err) {
      showNotification('Failed to update link status', 'error');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showNotification('Link copied to clipboard!', 'success');
  };

  const showNotification = (message, type) => {
    const notification = document.createElement('div');
    notification.className = `notification ${type}`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.classList.add('show');
      setTimeout(() => {
        notification.classList.remove('show');
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, 2000);
    }, 10);
  };

  const filteredLinks = links.filter(link => {
    const matchesFilter = filter === 'all' || link.status === filter;
    const matchesSearch = searchTerm === '' || 
      link.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.link_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return '#10b981';
      case 'inactive': return '#6b7280';
      case 'expired': return '#f59e0b';
      case 'used': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading payment links...</p>
      </div>
    );
  }

  return (
    <div className="admin-links-list">
      <div className="links-header">
        <div className="header-left">
          <h1>
            <span className="gradient-text">Payment Links</span>
            
            <span className="header-badge">{filteredLinks.length} total</span>
          </h1>
          <p className="header-subtitle">Manage and monitor all your payment links</p>
          <button 
          className="back-btn"
          onClick={() => navigate('/admin/dashboard')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Dashboard
        </button>
        </div>
        
        <button 
          className="create-btn"
          onClick={() => navigate('/admin/create-payment-link')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
          Create New Link
        </button>
      </div>

      <div className="links-controls">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            placeholder="Search by title, ID, or customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filters">
          {['all', 'active', 'inactive', 'expired', 'used'].map((filterType) => (
            <button 
              key={filterType}
              className={`filter-btn ${filter === filterType ? 'active' : ''}`}
              onClick={() => setFilter(filterType)}
            >
              {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
              {filterType !== 'all' && (
                <span className="filter-count">
                  {links.filter(l => l.status === filterType).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{error}</span>
        </div>
      )}

      

      <div className="links-table-container">
        {filteredLinks.length === 0 ? (
          <div className="empty-state">
            <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            <h3>No payment links found</h3>
            <p>{searchTerm ? 'Try adjusting your search or filter' : 'Get started by creating your first payment link'}</p>
            {!searchTerm && (
              <button 
                className="create-first-btn"
                onClick={() => navigate('/admin/create-payment-link')}
              >
                Create Payment Link
              </button>
            )}
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="links-table">
              <thead>
                <tr>
                  <th>Link Details</th>
                  <th>Customer</th>
                  <th>Usage</th>
                  <th>Status</th>
                  <th>Expiry</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLinks.map((link, index) => (
                  <tr key={link.link_id} className="table-row" style={{animationDelay: `${index * 0.05}s`}}>
                    <td>
                      <div className="link-details-cell">
                        <div className="link-title">
                          <span className="link-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                            </svg>
                          </span>
                          <div>
                            <strong>{link.title || 'Untitled Link'}</strong>
                            <div className="link-id">
                              <code>{link.link_id.substring(0, 8)}...</code>
                              <button 
                                className="copy-id-btn"
                                onClick={() => copyToClipboard(link.link_id)}
                                title="Copy Link ID"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="link-amount">₹{link.amount?.toLocaleString()}</div>
                      </div>
                    </td>
                    <td>
                      {link.customer_name ? (
                        <div className="customer-info">
                          <div className="customer-avatar">
                            {link.customer_name.charAt(0).toUpperCase()}
                          </div>
                          <div className="customer-details">
                            <span className="customer-name">{link.customer_name}</span>
                            {link.customer_email && (
                              <span className="customer-email">{link.customer_email}</span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="no-customer">No customer specified</span>
                      )}
                    </td>
                    <td>
                      <div className="usage-info">
                        <div className="usage-bar">
                          <div 
                            className="usage-progress"
                            style={{width: `${(link.payments_made / link.max_payments) * 100}%`}}
                          ></div>
                        </div>
                        <span className="usage-text">
                          {link.payments_made}/{link.max_payments} used
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${link.status}`}>
                        <span 
                          className="status-dot"
                          style={{backgroundColor: getStatusColor(link.status)}}
                        ></span>
                        {link.status}
                      </span>
                    </td>
                    <td>
                      {link.expiry_date ? (
                        <div className="expiry-info">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12 6 12 12 16 14"/>
                          </svg>
                          <span className={new Date(link.expiry_date) < new Date() ? 'expired' : ''}>
                            {new Date(link.expiry_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      ) : (
                        <span className="no-expiry">No expiry</span>
                      )}
                    </td>
                    <td>
                      <div className="created-info">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        <span>
                          {new Date(link.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn copy"
                          onClick={() => copyToClipboard(`${window.location.origin}/pay/${link.link_id}`)}
                          title="Copy payment link"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                          </svg>
                        </button>
                        
                        {/* {link.status === 'active' && (
                          <button
                            className="action-btn deactivate"
                            onClick={() => handleStatusChange(link.link_id, 'inactive')}
                            title="Deactivate link"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"/>
                              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                            </svg>
                          </button>
                        )} */}
                        
                        {link.status === 'inactive' && (
                          <button
                            className="action-btn activate"
                            onClick={() => handleStatusChange(link.link_id, 'active')}
                            title="Activate link"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18.36 6.64A9 9 0 0 1 20.77 15"/>
                              <path d="M6.16 6.16a9 9 0 1 0 12.68 12.68"/>
                              <circle cx="12" cy="12" r="2"/>
                            </svg>
                          </button>
                        )}
                        
                        <button
                          className="action-btn delete"
                          onClick={() => setShowDeleteConfirm(link.link_id)}
                          title="Delete link"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            <line x1="10" y1="11" x2="10" y2="17"/>
                            <line x1="14" y1="11" x2="14" y2="17"/>
                          </svg>
                        </button>

                        {/* <button
                          className="action-btn view"
                          onClick={() => navigate(`/admin/payment-link/${link.link_id}`)}
                          title="View details"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="2"/>
                            <path d="M22 12c-2.667 4.667-6 7-10 7s-7.333-2.333-10-7c2.667-4.667 6-7 10-7s7.333 2.333 10 7z"/>
                          </svg>
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="delete-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-icon warning">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h3>Delete Payment Link</h3>
            <p>Are you sure you want to delete this payment link? This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn"
                onClick={() => handleDelete(showDeleteConfirm)}
              >
                Delete Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentLinksList;